import React, {useCallback} from 'react';

import {parseSDF} from 'bulk-import/SpreadsheetBulkImportDAO';
import SpreadsheetUploadPane from 'spreadsheet-import-wizard/shared/SpreadsheetUploadPane';
import type {SpreadsheetUploadResult} from 'spreadsheet-import-wizard/shared/SpreadsheetUploadPane';
import * as ImportUtil from 'util/ImportUtil';

import type {ModalAction, ModalState} from './moleculeImportReducer';

const SDF_EXTENSIONS = ['.sdf', '.sd'];

function isSdfFile(file: File): boolean {
  const lower = file.name.toLowerCase();
  return SDF_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

interface FileUploadPaneProps {
  state: ModalState;
  dispatch: React.Dispatch<ModalAction>;
}

export default function FileUploadPane({dispatch}: FileUploadPaneProps): JSX.Element {
  const handleSdfUpload = useCallback(
    async (file: File): Promise<{s3Url: string; headers: Array<string>}> => {
      const s3Url = (await ImportUtil.upload(file))!;
      const result = (await parseSDF(file.name, s3Url))!;
      return {s3Url: result.updatedUrl, headers: result.columnNames};
    },
    []
  );

  const handleUploadComplete = useCallback(
    (result: SpreadsheetUploadResult) => {
      dispatch({
        type: 'SET_FILE_UPLOAD_RESULT',
        payload: {file: result.file, s3Url: result.s3Url, headers: result.headers},
      });
    },
    [dispatch]
  );

  const handleRemove = useCallback(() => {
    dispatch({type: 'CLEAR_FILE_UPLOAD'});
  }, [dispatch]);

  return (
    <SpreadsheetUploadPane
      accept=".csv,.xls,.xlsx,.sdf,.sd"
      title="Upload a CSV, Excel, or SDF file"
      customFileHandler={{shouldHandle: isSdfFile, handle: handleSdfUpload}}
      onUploadComplete={handleUploadComplete}
      onRemove={handleRemove}
    />
  );
}
